import { NavigationProp, useNavigation } from '@react-navigation/native';
import { usePin } from 'context/PinContext';
import { RootStackParamList } from 'navigation/AppNavigator.web';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useCreatePinMutation, useGetProfilesQuery } from 'services/backend';
import { cmsApiSlice } from 'services/cms';
import { RootState } from 'store/index';
import { Profile, selectProfileId } from 'store/slices/profileSlice';
import { MAX_PROFILES } from 'utils/constants';
import { calcSizeForDevice, getResponsiveSize } from 'utils/index';
import { getProfileHasPin } from 'utils/pin';
import { Platform } from 'utils/platform';

import { PinModal } from 'components/Pin/PinModal';
import { ProfileCard } from 'components/Profile/Card';
import { WebDiv } from 'components/WebDiv';
import IsLoading from 'components/isLoading';

const ProfileSelectionScreen = () => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const dispatch = useDispatch();
  const { requirePin, hasPin, isGetPinQueryLoading, openResetPin } = usePin();

  const { id: userId } = useSelector((state: RootState) => state.auth);
  const { data: profiles = [], isLoading: isProfilesQueryLoading } = useGetProfilesQuery(
    undefined,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    }
  );

  const [showPinModal, setShowPinModal] = useState(false);

  const [createPin, { isLoading: isCreatingPin }] = useCreatePinMutation();

  const visibleProfiles = useMemo(() => profiles.slice(0, MAX_PROFILES), [profiles]);
  const canAddProfile = visibleProfiles.length < MAX_PROFILES;

  const enterProfile = useCallback(
    (id: string) => {
      dispatch(selectProfileId(id));
      dispatch(cmsApiSlice.util.resetApiState());
    },
    [dispatch]
  );

  const handleAddProfile = useCallback(async () => {
    if (hasPin) {
      const isPinVerified = await requirePin();
      if (!isPinVerified) return;
    }
    navigation.navigate('ProfileCreate');
  }, [hasPin, requirePin, navigation]);

  const handleEditProfile = useCallback(
    async (id: string) => {
      if (hasPin) {
        const isPinVerified = await requirePin();
        if (!isPinVerified) return;
      }

      navigation.navigate('ProfileEdit', { profileId: id });
    },
    [hasPin, requirePin, navigation]
  );

  const handleSelectProfile = useCallback(
    async (profile: Profile) => {
      if (getProfileHasPin(profile)) {
        const isPinVerified = await requirePin();
        if (!isPinVerified) return;
      }

      enterProfile(profile.id);
    },
    [requirePin, enterProfile]
  );

  const handleSetupPin = async (pin: string) => {
    await createPin({ user: userId, pin }).unwrap();
    setShowPinModal(false);
  };

  const handleConfirmPin = async (pin: string) => {
    try {
      if (!hasPin) {
        await handleSetupPin(pin);
      }
    } catch (err) {
      console.error('Error on PIN:', err);
    }
  };

  const Container = Platform.isTV ? WebDiv : View;

  const containerProps = Platform.isTV
    ? { className: 'navigation-container navigation-priority' }
    : { style: styles.webContainer };

  useEffect(() => {
    if (!hasPin && !isGetPinQueryLoading) setShowPinModal(true);
  }, [hasPin, isGetPinQueryLoading]);

  if (isProfilesQueryLoading || isCreatingPin) {
    return (
      <View style={styles.loadingContainer}>
        <IsLoading />
      </View>
    );
  }

  return (
    <View style={!Platform.isTV && styles.webSelectionContainer}>
      <Text style={[styles.title, Platform.isTV ? styles.tvTitle : styles.webTitle]}>
        Quem está assistindo?
      </Text>
      <Container {...containerProps}>
        <View style={[styles.profileList, !Platform.isTV && styles.webProfileList]}>
          {visibleProfiles?.map((profile) => (
            <View style={Platform.isTV && styles.cardContainer} key={profile.id}>
              <ProfileCard
                type="profile"
                profile={profile}
                onSelect={() => handleSelectProfile(profile)}
                onEdit={() => handleEditProfile(profile.id)}
              />
            </View>
          ))}

          {canAddProfile && <ProfileCard type="add" onAdd={handleAddProfile} />}
        </View>
        {hasPin && (
          <View style={styles.forgotPinContainer}>
            <Pressable
              accessibilityRole="button"
              style={styles.forgotPinButton}
              onPress={openResetPin}>
              <Text style={styles.forgotPinText}>Esqueceu o PIN?</Text>
            </Pressable>
          </View>
        )}
      </Container>
      {showPinModal && !hasPin && (
        <PinModal variant="setup" isOpen={showPinModal} onConfirm={handleConfirmPin} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  webSelectionContainer: {
    width: '92%',
    alignSelf: 'center',
  },
  title: {
    fontWeight: '600',
    color: '#FFF',
    textAlign: 'center',
  },
  tvTitle: {
    fontSize: calcSizeForDevice(44),
    marginTop: calcSizeForDevice(55, 71),
  },
  webTitle: {
    fontSize: Number(getResponsiveSize({ base: 28, md: 38 })),
    marginTop: Number(getResponsiveSize({ base: 24, md: 55 })),
  },
  webContainer: {
    minHeight: '100%',
    flexDirection: 'column',
    alignSelf: 'center',
  },
  profileList: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  webProfileList: {
    gap: 16,
  },
  cardContainer: {
    marginRight: calcSizeForDevice(64),
  },
  forgotPinContainer: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Platform.isTV ? calcSizeForDevice(150) : 'auto',
  },
  forgotPinButton: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: calcSizeForDevice(12),
    paddingHorizontal: calcSizeForDevice(16),
  },
  forgotPinText: {
    fontSize: calcSizeForDevice(16, 24),
    fontWeight: '500',
    color: '#A8B4FF',
  },
  loadingContainer: {
    width: '100%',
    height: calcSizeForDevice(560),
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ProfileSelectionScreen;
